import Avatar from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import TableRowAction from 'components/common/TableRowAction';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { weDonarItems, weUserImage } from 'data/weraise-data/donor-data';
import { getDateRange, parseRouteParams } from 'helpers/utils';
import { useCallback, useContext, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import paths from 'routes/paths';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import DonorCounter from './DonorCounter';
import FilterModal from 'components/common/FilterModal';
import useSessionState from 'hooks/useSessionState';
import { useDonorSearch, useDeleteDonor } from 'hooks/useDonors';
import { useDonorsCount } from 'hooks/useDashboard';
import AuthContext from 'context/authContext';

/* -------------------------------------------------------------------------- */

// Helper function to get country from phone number
const getCountryFromPhone = phone => {
  if (!phone) return 'Unknown';

  // Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, '');

  // Common country codes mapping
  const countryCodes = {
    1: 'USA/Canada',
    44: 'UK',
    91: 'India',
    86: 'China',
    81: 'Japan',
    82: 'South Korea',
    33: 'France',
    49: 'Germany',
    39: 'Italy',
    34: 'Spain',
    61: 'Australia',
    55: 'Brazil',
    52: 'Mexico',
    27: 'South Africa',
    234: 'Nigeria',
    254: 'Kenya',
    256: 'Uganda',
    255: 'Tanzania',
    233: 'Ghana',
    251: 'Ethiopia',
    212: 'Morocco',
    213: 'Algeria',
    216: 'Tunisia',
    218: 'Libya',
    20: 'Egypt',
    249: 'Sudan',
    252: 'Somalia',
    253: 'Djibouti',
    257: 'Burundi',
    258: 'Mozambique',
    260: 'Zambia',
    263: 'Zimbabwe',
    265: 'Malawi',
    266: 'Lesotho',
    267: 'Botswana',
    268: 'Eswatini',
    269: 'Comoros',
    90: 'Turkey',
    92: 'Pakistan',
    93: 'Afghanistan',
    94: 'Sri Lanka',
    95: 'Myanmar',
    960: 'Maldives',
    961: 'Lebanon',
    962: 'Jordan',
    963: 'Syria',
    964: 'Iraq',
    965: 'Kuwait',
    966: 'Saudi Arabia',
    967: 'Yemen',
    968: 'Oman',
    970: 'Palestine',
    971: 'UAE',
    972: 'Israel',
    973: 'Bahrain',
    974: 'Qatar',
    977: 'Nepal',
    880: 'Bangladesh'
  };

  // Check for country codes (longest match first)
  for (let i = 4; i >= 1; i--) {
    const prefix = cleanPhone.substring(0, i);
    if (countryCodes[prefix]) {
      return countryCodes[prefix];
    }
  }

  return 'Unknown';
};

const columns = (handleDelete, navigate) => [
  {
    accessor: 'simple_id',
    Header: 'ID',
    cellProps: {
      className: 'py-2'
    }
  },
  {
    accessor: 'name',
    Header: 'Name',
    cellProps: {
      className: 'py-2'
    },
    Cell: rowData => {
      const { id, name } = rowData.row.original;
      return (
        <Flex alignItems="center">
          <Avatar src={weUserImage} rounded="circle" size="xl" />
          <Link
            to={`/donors/${id}`}
            className="fw-medium ms-2 text-1100 hover-primary"
          >
            {name}
          </Link>
        </Flex>
      );
    }
  },
  {
    accessor: 'email',
    Header: 'Email',
    cellProps: {
      className: 'py-2'
    },
    Cell: rowData => {
      const { email } = rowData.row.original;
      return (
        <a className="fw-medium text-600" href={'mailto:' + email}>
          {email}
        </a>
      );
    }
  },
  {
    accessor: 'country',
    Header: 'Country',
    cellProps: {
      className: 'py-2'
    },
    Cell: rowData => {
      const { country } = rowData.row.original;
      return <span className="fw-medium text-600">{country}</span>;
    }
  },
  {
    accessor: 'phone',
    Header: 'Phone',
    cellProps: {
      className: 'py-2'
    },
    Cell: rowData => {
      const { phone } = rowData.row.original;

      return (
        <a className="fw-medium text-600" href={'callTo:' + phone}>
          {phone}
        </a>
      );
    }
  },
  {
    accessor: 'address',
    Header: 'Address',
    cellProps: {
      className: 'fw-medium text-600 py-2'
    },
    Cell: rowData => {
      const { address } = rowData.row.original;
      return <span className="fw-medium text-600">{address}</span>;
    }
  },
  {
    accessor: 'payment',
    Header: 'Payment',
    cellProps: {
      className: 'fw-medium py-2'
    },

    Cell: rowData => (
      <SoftBadge pill bg={'success'} className="me-2 px-2">
        {rowData.row.original.payment}
      </SoftBadge>
    )
  },
  {
    accessor: 'id_',
    Header: '',
    cellProps: {
      className: 'py-2 text-end'
    },
    disableSortBy: true,

    Cell: rowData => {
      const { id } = rowData.row.original;
      return (
        <TableRowAction
          onCLickEdit={() =>
            navigate(parseRouteParams(paths.editDonor, { id }))
          }
          onCLickDelete={() => handleDelete(id)}
          subject={permissionsKeyMap.donor.children.donors}
        />
      );
    }
  }
];

const useQueryParams = () => {
  return new URLSearchParams(useLocation().search);
};

const Donors = () => {
  const navigate = useNavigate();
  const { selectedBranchId, isAllBranches } = useContext(AuthContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const queryParams = useQueryParams();
  // Date filter, search term and page are kept for this tab, so coming back
  // from a donor's profile lands on the same list
  const [donorFilters, setDonorFilters] = useSessionState(
    'filters:donors',
    {}
  );

  // Parse simple_id params from URL
  const simpleIdParams = useMemo(() => {
    const ids = queryParams.get('simple_id')?.split(',') || [];
    return ids.map(id => parseInt(id, 10));
  }, [queryParams]);

  // Server-side pagination + search state, owned here and driven by the table.
  const [serverState, setServerState] = useSessionState(
    'filters:donors:table',
    {
      pageIndex: 0,
      pageSize: 50,
      globalFilter: ''
    }
  );

  const handleServerStateChange = useCallback(
    ({ pageIndex, pageSize, globalFilter }) => {
      const gf = globalFilter || '';
      setServerState(prev =>
        prev.pageIndex === pageIndex &&
        prev.pageSize === pageSize &&
        prev.globalFilter === gf
          ? prev
          : { pageIndex, pageSize, globalFilter: gf }
      );
    },
    [setServerState]
  );

  // One page of donors from the server. Search runs against ALL donors in the
  // database (not just the loaded page), and totals come back in the same call.
  const { donors, totalCount, isLoading } = useDonorSearch({
    search: serverState.globalFilter,
    page: serverState.pageIndex,
    pageSize: serverState.pageSize,
    startDate: donorFilters.startDate || null,
    endDate: donorFilters.endDate || null,
    simpleIds: simpleIdParams.length > 0 ? simpleIdParams : null
  });

  const pageCount = Math.max(1, Math.ceil(totalCount / serverState.pageSize));

  // Remount the table (resetting it to page 1) when the date/simple_id filters
  // change, so applying a filter never strands you on an out-of-range page.
  const tableResetKey = `${donorFilters.startDate || ''}|${
    donorFilters.endDate || ''
  }|${simpleIdParams.join(',')}`;

  // Fetch counter stats via lightweight count queries
  const { count: totalDonors } = useDonorsCount();

  const monthDateFilter = useMemo(() => {
    const range = getDateRange('month');
    return { startDate: range.startDate, endDate: range.endDate };
  }, []);

  const weekDateFilter = useMemo(() => {
    const range = getDateRange('week');
    return { startDate: range.startDate, endDate: range.endDate };
  }, []);

  const { count: monthlyDonorCount } = useDonorsCount(monthDateFilter);
  const { count: weeklyDonorCount } = useDonorsCount(weekDateFilter);

  // Delete donor hook
  const { deleteDonor, isDeleting } = useDeleteDonor();

  // Transform donors data for table (handles both app-created and migrated donor formats)
  const tableData = useMemo(() => {
    if (!donors || donors.length === 0) return [];
    return donors.map(item => {
      return {
        key: item.id,
        id: item.id,
        simple_id: item.simple_id,
        name: item.first_name + ' ' + item.last_name,
        email: item.primary_email || '',
        phone: item.primary_phone || '',
        country: getCountryFromPhone(item.primary_phone || ''),
        address: item.primary_address || '',
        payment: `£${item?.total_donation ?? 0}`
      };
    });
  }, [donors]);

  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteModalHide = () => {
    setSelectedId(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = () => {
    deleteDonor(selectedId);
    handleDeleteModalHide();
  };

  const handleFilter = () => {
    setShowFilterModal(true);
  };

  const getDonorFilterRange = option => {
    const now = new Date();
    let startDate;
    let endDate;

    switch (option) {
      case 'today':
        startDate = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        endDate = new Date(now.setHours(23, 59, 59, 999)).toISOString();
        break;

      case 'week':
        endDate = new Date(now.setHours(23, 59, 59, 999)).toISOString();
        const startOfWeek = new Date();
        startOfWeek.setDate(now.getDate() - 7);
        startOfWeek.setHours(0, 0, 0, 0);
        startDate = startOfWeek.toISOString();
        break;

      case 'month':
        startDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        ).toISOString();
        endDate = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0
        ).toISOString();
        break;

      default:
        // eslint-disable-next-line no-multi-assign
        startDate = endDate = null;
    }
    return { startDate, endDate };
  };

  const applyFilter = async (dateRangeValue, data) => {
    const { startDate, endDate } = getDonorFilterRange(dateRangeValue || '');
    const newFilters = {};

    if (startDate && endDate) {
      newFilters.startDate = startDate;
      newFilters.endDate = endDate;
    } else if (
      dateRangeValue === 'select_date' &&
      data.startDate &&
      data.endDate
    ) {
      const startOfSelectedDate = new Date(data.startDate).setHours(0, 0, 0, 0);
      const endOfSelectedDate = new Date(data.endDate).setHours(
        23,
        59,
        59,
        999
      );
      newFilters.startDate = new Date(startOfSelectedDate).toISOString();
      newFilters.endDate = new Date(endOfSelectedDate).toISOString();
    }

    setDonorFilters(newFilters);
    // A new date filter remounts the table on page 1 (see tableResetKey)
    setServerState(prev => ({ ...prev, pageIndex: 0 }));
    setShowFilterModal(false);
  };

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal}
        onHide={handleDeleteModalHide}
        loading={isDeleting}
        title="Delete Donor"
        handleConfirm={handleConfirmDelete}
        body="Are you sure you want to delete selected donor?"
      />

      {showFilterModal && (
        <FilterModal
          show={showFilterModal}
          onHide={() => setShowFilterModal(false)}
          applyFilter={applyFilter}
        />
      )}

      <Row>
        <Col>
          <DonorCounter
            counterData={weDonarItems({
              totalDonors,
              newDonor: monthlyDonorCount,
              monthlyHighest: {},
              weeklyHighest: {},
              newDonorWeek: weeklyDonorCount
            })}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            key={tableResetKey}
            data={tableData}
            columns={columns(handleDeleteClick, navigate)}
            title="Donors list"
            addButtonLabel="Add New Donor"
            subject={permissionsKeyMap.donor.children.donors}
            showSearchInput
            searchInputPlaceHolder="Search all donors by name, email, phone or ID"
            onClickAddNew={() => navigate(paths.addNewDonar)}
            loading={isLoading}
            addNew={selectedBranchId !== 'all' && !isAllBranches}
            hasFilters
            handleFilter={handleFilter}
            perPage={serverState.pageSize}
            manualPagination
            controlledPageCount={pageCount}
            initialPageIndex={serverState.pageIndex}
            initialGlobalFilter={serverState.globalFilter}
            onServerStateChange={handleServerStateChange}
          />
        </Col>
      </Row>
    </>
  );
};

export default Donors;
